/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAccessToken, refreshAccessToken } from '../lib/axios';
import type { UserQuota } from '../types';

export type ConversationTitleUpdatedEvent = {
  type: 'conversation.title_updated';
  userId: number;
  data: {
    sessionId: number;
    sessionPublicId: string;
    title: string;
    status: string;
    updatedAt: string;
    lastMessageAt: string | null;
    agentPublicId: string | null;
    agentName: string | null;
  };
};

export type NotificationCreatedEvent = {
  type: 'notification.created';
  audience: 'all' | 'users';
  userIds?: number[];
  data: { publicId: string; category: string; severity: string; publishedAt: string };
};

export type QuotaUpdatedEvent = {
  type: 'quota.updated';
  userId: number;
  data: UserQuota;
};

export type AppRealtimeEvent =
  | ConversationTitleUpdatedEvent
  | NotificationCreatedEvent
  | QuotaUpdatedEvent;

type EventHandler<T extends AppRealtimeEvent = AppRealtimeEvent> = (event: T) => void;
type SubscriptionMap = Map<AppRealtimeEvent['type'], Set<EventHandler>>;

type AppRealtimeContextValue = {
  subscribe: <T extends AppRealtimeEvent['type']>(
    type: T,
    handler: EventHandler<Extract<AppRealtimeEvent, { type: T }>>
  ) => () => void;
  connected: boolean;
  reconnectCount: number;
};

const AppRealtimeContext = createContext<AppRealtimeContextValue | null>(null);

const buildRealtimeUrl = (token: string) => {
  const baseUrl =
    import.meta.env.VITE_SERVER_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const url = new URL('/v1/chat/realtime/ws', baseUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);
  return url.toString();
};

const parseRealtimeEvent = (data: unknown): AppRealtimeEvent | null => {
  if (typeof data !== 'string' || data === 'pong') {
    return null;
  }

  try {
    const parsed = JSON.parse(data) as Partial<AppRealtimeEvent>;
    return parsed.type === 'conversation.title_updated' ||
      parsed.type === 'notification.created' ||
      parsed.type === 'quota.updated'
      ? (parsed as AppRealtimeEvent)
      : null;
  } catch {
    return null;
  }
};

const reconnectDelay = (attempt: number) =>
  Math.min(30000, 1000 * 2 ** attempt) + Math.random() * 3000;
export function AppRealtimeProvider(props: {
  enabled: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const { enabled, children } = props;
  const subscribersRef = useRef<SubscriptionMap>(new Map());
  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const openCountRef = useRef(0);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const pingTimerRef = useRef<number | undefined>(undefined);
  const stableOpenTimerRef = useRef<number | undefined>(undefined);
  const [connected, setConnected] = useState(false);
  const [reconnectCount, setReconnectCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }

    let stopped = false;

    const clearTimer = (timer: React.MutableRefObject<number | undefined>) => {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
        timer.current = undefined;
      }
    };

    const clearIntervalTimer = () => {
      if (pingTimerRef.current !== undefined) {
        window.clearInterval(pingTimerRef.current);
        pingTimerRef.current = undefined;
      }
    };

    const clearTimers = () => {
      clearTimer(reconnectTimerRef);
      clearTimer(stableOpenTimerRef);
      clearIntervalTimer();
    };

    const scheduleReconnect = () => {
      if (stopped || reconnectTimerRef.current !== undefined) {
        return;
      }

      const delay = reconnectDelay(attemptRef.current);
      attemptRef.current += 1;
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = undefined;
        void connect();
      }, delay);
    };

    const dispatch = (event: AppRealtimeEvent) => {
      const handlers = subscribersRef.current.get(event.type);
      if (!handlers) {
        return;
      }

      handlers.forEach((handler) => handler(event));
    };

    const connect = async () => {
      try {
        const token = getAccessToken() || (await refreshAccessToken());
        if (stopped) return;

        const socket = new WebSocket(buildRealtimeUrl(token));
        socketRef.current = socket;

        socket.onopen = () => {
          setConnected(true);
          openCountRef.current += 1;
          if (openCountRef.current > 1) {
            setReconnectCount((count) => count + 1);
          }

          clearIntervalTimer();
          clearTimer(stableOpenTimerRef);
          stableOpenTimerRef.current = window.setTimeout(() => {
            attemptRef.current = 0;
            stableOpenTimerRef.current = undefined;
          }, 10000);

          pingTimerRef.current = window.setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send('ping');
            }
          }, 25000);
        };

        socket.onmessage = (messageEvent) => {
          const event = parseRealtimeEvent(messageEvent.data);
          if (event) {
            dispatch(event);
          }
        };

        socket.onclose = () => {
          if (socketRef.current === socket) {
            socketRef.current = null;
          }
          setConnected(false);
          clearIntervalTimer();
          clearTimer(stableOpenTimerRef);
          scheduleReconnect();
        };

        socket.onerror = () => {
          socket.close();
        };
      } catch {
        setConnected(false);
        scheduleReconnect();
      }
    };

    void connect();

    return () => {
      stopped = true;
      setConnected(false);
      clearTimers();
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled]);

  const value = useMemo<AppRealtimeContextValue>(
    () => ({
      subscribe: (type, handler) => {
        const subscribers = subscribersRef.current;
        const handlers = subscribers.get(type) ?? new Set<EventHandler>();
        handlers.add(handler as EventHandler);
        subscribers.set(type, handlers);

        return () => {
          handlers.delete(handler as EventHandler);
          if (handlers.size === 0) {
            subscribers.delete(type);
          }
        };
      },
      connected,
      reconnectCount,
    }),
    [connected, reconnectCount]
  );

  return <AppRealtimeContext.Provider value={value}>{children}</AppRealtimeContext.Provider>;
}

export function useAppRealtimeEvent<T extends AppRealtimeEvent['type']>(
  type: T,
  handler: (event: Extract<AppRealtimeEvent, { type: T }>) => void
): void {
  const context = useContext(AppRealtimeContext);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!context) {
      return undefined;
    }

    return context.subscribe(type, (event) => handlerRef.current(event));
  }, [context, type]);
}

export function useAppRealtimeStatus(): { connected: boolean; reconnectCount: number } {
  const context = useContext(AppRealtimeContext);
  return context
    ? { connected: context.connected, reconnectCount: context.reconnectCount }
    : { connected: false, reconnectCount: 0 };
}
